import React, { createContext, useContext, ReactNode, useCallback } from 'react';

interface AuthContextType {
  isAuthenticated: boolean;
  setIsAuthenticated: (isAuthenticated: boolean, source?: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Log authentication state changes
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  // Create a wrapped version of setIsAuthenticated with logging
  const { isAuthenticated, setIsAuthenticated: originalSetIsAuthenticated } = context;
  
  const wrappedSetIsAuthenticated = useCallback((value: boolean, source = 'unknown') => {
    console.group(`[Auth][${new Date().toISOString()}] Auth State Update (${source})`);
    console.log(`Previous state: ${isAuthenticated}`);
    console.log(`New state: ${value}`);
    console.log(`Call stack:`, new Error().stack?.split('\n').slice(1, 4).join('\n'));
    console.groupEnd();
    
    // Store in localStorage for debugging
    localStorage.setItem('auth-debug-last-change', JSON.stringify({
      timestamp: new Date().toISOString(),
      from: isAuthenticated,
      to: value,
      source,
      url: window.location.href
    }));
    
    originalSetIsAuthenticated(value);
  }, [isAuthenticated, originalSetIsAuthenticated]);
  
  return {
    isAuthenticated,
    setIsAuthenticated: wrappedSetIsAuthenticated
  };
};

interface AuthProviderProps {
  children: ReactNode;
  value: {
    isAuthenticated: boolean;
    setIsAuthenticated: (isAuthenticated: boolean, source?: string) => void;
  };
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children, value }) => {
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
